import { AIService } from './AIService';
import { 
  PlaylistData, 
  PlaylistGenerationOptions, 
  ChatResponse 
} from './types';
import { log } from './utils';

export class PlaylistGenerationService {
  constructor(private aiService: AIService) {}

  // Generate a playlist based on a theme or description with enhanced parameters
  public async generatePlaylist(
    description: string,
    options?: PlaylistGenerationOptions
  ): Promise<ChatResponse> {
    // Enhance the prompt with user preferences if provided
    let enhancedPrompt = `Create a playlist based on this theme or description: "${description}".`;
    
    if (options?.favoriteTracks && options.favoriteTracks.length > 0) {
      enhancedPrompt += `\nSome of my favorite tracks: ${options.favoriteTracks.join(", ")}`;
    }
    
    if (options?.favoriteArtists && options.favoriteArtists.length > 0) {
      enhancedPrompt += `\nSome of my favorite artists: ${options.favoriteArtists.join(", ")}`;
    }
    
    if (options?.mood) {
      enhancedPrompt += `\nMood: ${options.mood}`;
    }
    
    if (options?.activity) {
      enhancedPrompt += `\nActivity: ${options.activity}`;
    }
    
    if (options?.era) {
      enhancedPrompt += `\nEra preference: ${options.era}`;
    }
    
    if (options?.tempo) {
      enhancedPrompt += `\nTempo preference: ${options.tempo}`;
    }
    
    if (options?.genreDiversity) {
      enhancedPrompt += `\nGenre diversity: ${options.genreDiversity}`;
    }
    
    if (options?.includeObscure) {
      enhancedPrompt += `\nInclude some lesser-known/obscure tracks`;
    }
    
    const trackCount = options?.trackCount || 12;
    enhancedPrompt += `\nPlease include approximately ${trackCount} tracks.`;
    
    enhancedPrompt += `\nPlease format the response as a JSON object with the following structure:
    {
      "playlist_name": "Suggested name",
      "description": "Brief description that captures the essence of the playlist",
      "tracks": [
        {"artist": "Artist name", "track": "Track name", "reason": "Brief reason for inclusion that connects to the theme"}
      ]
    }`;
    
    log('Generating playlist with prompt:', enhancedPrompt);
    const response = await this.aiService.sendMessage(enhancedPrompt);
    
    // If verifyWithSpotify is true, verify the tracks with Spotify
    if (options?.verifyWithSpotify) {
      const playlistData = this.aiService.extractPlaylistFromResponse(response.message.content);
      if (playlistData) {
        const verifiedPlaylist = await this.verifyPlaylistWithSpotify(playlistData);
        
        // Create a new response with the verified playlist
        const verifiedResponse = {
          message: {
            role: 'assistant' as const,
            content: `Here's a verified playlist based on your request:\n\n${JSON.stringify(verifiedPlaylist, null, 2)}`
          }
        };
        
        return verifiedResponse;
      }
    }
    
    return response;
  }
  
  // Verify a playlist with Spotify and enhance it with real track data
  public async verifyPlaylistWithSpotify(playlist: PlaylistData): Promise<PlaylistData> {
    log('Verifying playlist with Spotify:', playlist.playlist_name);
    
    try {
      // Import spotifyService dynamically to avoid circular dependencies
      const { spotifyService } = await import('../spotify');
      
      const verifiedTracks = [];
      
      for (const track of playlist.tracks) {
        try {
          // Search for the track on Spotify
          const searchQuery = `${track.track} ${track.artist}`;
          const searchResults = await spotifyService.search(searchQuery, 'track', 1);
          
          if (searchResults.tracks && searchResults.tracks.length > 0) {
            // Found a match on Spotify
            const spotifyTrack = searchResults.tracks[0];
            
            // Create a verified track with additional Spotify data
            verifiedTracks.push({
              artist: spotifyTrack.artists[0].name,
              track: spotifyTrack.name,
              reason: track.reason,
              spotify_uri: spotifyTrack.uri,
              album: spotifyTrack.album.name,
              release_date: spotifyTrack.album.releaseDate,
              popularity: spotifyTrack.popularity,
              preview_url: spotifyTrack.previewUrl,
              image_url: spotifyTrack.imageUrl
            });
            
            log(`Verified track: ${spotifyTrack.name} by ${spotifyTrack.artists[0].name}`);
          } else {
            // No match found, keep the original track but mark it
            verifiedTracks.push({
              ...track,
              verified: false,
              note: "Track not found on Spotify"
            });
            
            log(`Track not found: ${track.track} by ${track.artist}`);
          }
        } catch (error) {
          log(`Error verifying track ${track.track} by ${track.artist}:`, error);
          
          // Keep the original track but mark the error
          verifiedTracks.push({
            ...track,
            verified: false,
            note: "Error verifying with Spotify"
          });
        }
      }
      
      // Return the enhanced playlist with verified tracks
      return {
        ...playlist,
        tracks: verifiedTracks,
        verified: true,
        verification_timestamp: new Date().toISOString()
      };
    } catch (error) {
      log('Error verifying playlist with Spotify:', error);
      
      // Return the original playlist if verification fails
      return {
        ...playlist,
        verified: false,
        verification_error: "Failed to connect to Spotify API"
      };
    }
  }
}
